// Review Specification per analysis definition (PRD-003 Area 1 / SPEC-007): which competence
// group(s) may perform the second-person review of this analysis, and how long the review takes.
//
// This lives OUTSIDE the analysis's Task Design flow — it is not a procedure step and must NOT
// count toward the analysis Minimum Duration. It feeds the "Review <analysis>" single-action that
// is created when an analyst sends results to review (see limsReviews.ensureForAnalysis).
//
// SPEC-021 (B2): there is NO /lims/reviewSpec endpoint. The review spec is FOLDED onto the
// analysis-definition payload — it arrives as the nested `reviewSpec` key on every definition
// dump and is WRITTEN via the definition save. This slice keeps the selector API (B-D3) and
// mirrors the definitions into the same map every consumer already reads.

// Fallback duration when an analysis definition has no Review Specification (no competence group
// set in its template design): the review lasts two hours. The competence groups fall back to
// "any member" — resolved to all competence groups at creation time so IAP can still place it.
export const REVIEW_FALLBACK_DURATION_SECONDS = 2 * 60 * 60

// state shape: { [analysisDefinitionId]: { allowedGroupIds: [...], duration: <seconds> | null } }
const initialState = {}

function normalizeGroupIds(ids) {
  return Array.isArray(ids) ? ids.filter((id) => typeof id === "string" && id) : []
}

// A duration is a positive number of seconds, or null when unset (→ fallback applies).
function normalizeDuration(d) {
  const n = Number(d)
  return Number.isFinite(n) && n > 0 ? n : null
}

function normalizeSpec(item) {
  return {
    allowedGroupIds: normalizeGroupIds(item && item.allowedGroupIds),
    duration: normalizeDuration(item && item.duration),
  }
}

// Whether a definition dump carries a usable nested review spec (null/absent = no row).
function reviewSpecOf(definition) {
  return definition && definition.reviewSpec && typeof definition.reviewSpec === "object"
    ? definition.reviewSpec
    : null
}

// Mirror one definition's nested review spec into the map (or drop the entry when it has none).
function applyDefinition(map, definition) {
  if (!definition || !definition.id) return map
  const spec = reviewSpecOf(definition)
  const next = { ...map }
  if (spec) {
    next[definition.id] = normalizeSpec(spec)
  } else if (next[definition.id]) {
    delete next[definition.id]
  }
  return next
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "analysisDefinitions/fetch": {
      const map = {}
      ;(action.res || []).forEach((item) => {
        const spec = reviewSpecOf(item)
        if (spec && item.id) {
          map[item.id] = normalizeSpec(spec)
        }
      })
      return map
    }
    case "analysisDefinitions/create":
    case "analysisDefinitions/edit":
      return applyDefinition(state, action.res)
    case "analysisDefinitions/remove": {
      if (!action.res || !state[action.res.id]) return state
      const next = { ...state }
      delete next[action.res.id]
      return next
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// The review spec for one analysis definition (or undefined if none stored yet).
export const selectReviewSpec = (state, analysisDefinitionId) => state.limsReviewSpec[analysisDefinitionId]
